import { createContext, useCallback, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { Toast as ToastUI, type AlertVariant } from '@orion/ui';

interface ToastItem {
  id: string;
  variant: AlertVariant;
  message: string;
}

interface ToastContextValue {
  show: (variant: AlertVariant, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
const AUTO_DISMISS_MS = 5000;

/** `@orion/ui`'s `Toast` is purely presentational — "callers own the
 * timer/visibility" per its own doc comment — and had no queue driving it
 * anywhere in the app, so every success/error message so far has been a
 * static inline `Alert` instead. This is that missing queue: mounted once
 * at the app root (main.tsx), fixed to the top-right corner, auto-dismisses
 * after 5s or on click. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (variant: AlertVariant, message: string) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, variant, message }]);
      setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    show,
    success: (message) => show('success', message),
    error: (message) => show('danger', message),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-100 flex w-full max-w-sm flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastUI variant={t.variant} onDismiss={() => dismiss(t.id)}>
              {t.message}
            </ToastUI>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
